// Main App Component
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { useAuthStore } from './lib/store';
import { authApi, setupApi } from './lib/api';
import { useInactivityLogout } from './hooks/useInactivityLogout';

// Pages
import LoginPage from './pages/LoginPage';
import SetupPage from './pages/SetupPage';
import DashboardPage from './pages/DashboardPage';
import MitarbeiterPage from './pages/MitarbeiterPage';
import FahrzeugePage from './pages/FahrzeugePage';
import EinteilungPage from './pages/EinteilungPage';
import StatistikPage from './pages/StatistikPage';
import AdminPage from './pages/AdminPage';
import ProfilPage from './pages/ProfilPage';
import DeveloperLoginPage from './pages/DeveloperLoginPage';
import DeveloperPage from './pages/DeveloperPage';
import AtemschutzPage from './pages/AtemschutzPage';
import PoolKleidungPage from './pages/PoolKleidungPage';
import PraktikantenPage from './pages/PraktikantenPage';
import SchrankAnsichtPage from './pages/SchrankAnsichtPage';
import SchrankVerwaltungPage from './pages/SchrankVerwaltungPage';
import SchrankRaumverwaltungPage from './pages/SchrankRaumverwaltungPage';
import DienstplanImportPage from './pages/DienstplanImportPage';
import MitarbeiterKategorienPage from './pages/MitarbeiterKategorienPage';
import RollePermissionsPage from './pages/RollePermissionsPage';
import BenachrichtigungsZielgruppenPage from './pages/BenachrichtigungsZielgruppenPage';
import SachgebietePage from './pages/SachgebietePage';

// Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Global Inactivity Guard – 1h Auto-Logout für eingeloggte User
function InactivityGuard({ children }) {
  useInactivityLogout();
  return children;
}

// Protected Route Wrapper
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  // Don't show loading state for too long - assume not authenticated after check
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Laden...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // For developer routes, redirect to developer login
    if (window.location.pathname.startsWith('/developer')) {
      return <Navigate to="/developer-login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.rolle)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// App Initializer
function AppInitializer({ children }) {
  const { setUser, clearUser, setLoading } = useAuthStore();
  const [needsSetup, setNeedsSetup] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Check if setup is needed
        const setupRes = await setupApi.getStatus();
        setNeedsSetup(setupRes.data.needs_setup);

        // HttpOnly cookies are intentionally invisible to JavaScript. Always
        // ask the backend whether a valid session exists.
        if (!setupRes.data.needs_setup) {
          try {
            const userRes = await authApi.me();
            setUser(userRes.data);
          } catch (error) {
            clearUser();
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Initialization error:', error);
        setLoading(false);
      } finally {
        setIsChecking(false);
        setLoading(false);
      }
    };

    initialize();
  }, [setUser, clearUser, setLoading]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Lade Digitale Feuerwache...</p>
        </div>
      </div>
    );
  }

  // Redirect to setup if needed
  if (needsSetup && window.location.pathname !== '/setup') {
    window.location.href = '/setup';
    return null;
  }

  return children;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppInitializer>
          <InactivityGuard>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/developer-login" element={<DeveloperLoginPage />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mitarbeiter"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN', 'MITARBEITER']}>
                  <MitarbeiterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mitarbeiter/sachgebiete"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN', 'MITARBEITER']}>
                  <SachgebietePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fahrzeuge"
              element={
                <ProtectedRoute>
                  <FahrzeugePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/einteilung"
              element={
                <ProtectedRoute>
                  <EinteilungPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/einteilung/import"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <DienstplanImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/atemschutz"
              element={
                <ProtectedRoute>
                  <AtemschutzPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pool-kleidung"
              element={
                <ProtectedRoute>
                  <PoolKleidungPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/praktikanten"
              element={
                <ProtectedRoute>
                  <PraktikantenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schrank"
              element={
                <ProtectedRoute>
                  <SchrankAnsichtPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schrank/verwaltung"
              element={
                <ProtectedRoute>
                  <SchrankVerwaltungPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schrank/raumverwaltung"
              element={
                <ProtectedRoute>
                  <SchrankRaumverwaltungPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/statistik"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN', 'MITARBEITER']}>
                  <StatistikPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <Navigate to="/developer/tags" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profil"
              element={
                <ProtectedRoute>
                  <ProfilPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <DeveloperPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/kategorien"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <MitarbeiterKategorienPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/rechte"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <RollePermissionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/benachrichtigungen"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <BenachrichtigungsZielgruppenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/einstellungen"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <Navigate to="/developer/tags" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developer/tags"
              element={
                <ProtectedRoute allowedRoles={['DEVELOPER']}>
                  <AdminPage />
                </ProtectedRoute>
              }
            />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </InactivityGuard>
        </AppInitializer>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
